import React, { useState, useEffect } from 'react';
import { BookOpen } from 'lucide-react';

const toMinutes = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const PRAYER_ORDER = ['shacharit', 'mincha', 'maariv', 'other'];
const PRAYER_LABEL = { shacharit: 'שחרית', mincha: 'מנחה', maariv: 'מעריב' };

const ZMAN_SHORT = {
  daySunset: 'השקיעה', dayNetz: 'הנץ', dayAlot: 'עלות השחר', dayChatzot: 'חצות',
  dayMinchaGedola: 'מנחה גדולה', dayMinchaKetana: 'מנחה קטנה', dayPlagHamincha: 'פלג המנחה',
  dayTzeit7083: 'צאת הכוכבים', dayTzeit85: 'צאת הכוכבים', dayTzeit42: 'צאת הכוכבים',
  dayTzeit50: 'צאת הכוכבים', dayTzeit72: 'צאת הכוכבים',
  daySofZmanShma: 'סוף זמן שמע', daySofZmanShmaMGA: 'סוף זמן שמע',
  daySofZmanTfilla: 'סוף זמן תפילה', daySofZmanTfillaMGA: 'סוף זמן תפילה',
};
const formatTimeRule = (ct) => {
  if (ct.type === 'fixed') return 'שעה קבועה';
  if (ct.type === 'relative') {
    const label = ZMAN_SHORT[ct.baseTime] || ct.baseTime;
    const offset = parseInt(ct.offset) || 0;
    if (offset === 0) return label;
    const abs = Math.abs(offset);
    return offset < 0 ? `${abs} דק׳ לפני ${label}` : `${abs} דק׳ אחרי ${label}`;
  }
  return null;
};
const DAY_NAMES = { 0: 'א׳', 1: 'ב׳', 2: 'ג׳', 3: 'ד׳', 4: 'ה׳', 5: 'ו׳', roshChodesh: 'ר״ח' };

const todayDow = new Date().getDay();
const todayHebrewDay = (() => { try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); } catch { return 0; } })();
const isRoshChodeshToday = todayHebrewDay === 1 || todayHebrewDay === 30;
const isOverrideActive = (days) => (days || []).includes(todayDow) || (isRoshChodeshToday && (days || []).includes('roshChodesh'));

const Modern = ({ processedCustomTimes, loading, error, title = 'תפילות יום חול', showTitle = true, recurringDisplay = 'compact', showRuleMode = false, highlightNextMinyan = false }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return <div style={styles.state}>טוען זמני תפילה...</div>;
  }

  if (error) {
    return <div style={{ ...styles.state, color: '#D32F2F' }}>{error}</div>;
  }

  if (!processedCustomTimes?.length) {
    return <div style={styles.state}>אין זמני תפילה</div>;
  }

  const nowMin = now.getHours() * 60 + now.getMinutes();

  const sorted = [...processedCustomTimes].sort((a, b) => {
    const ai = PRAYER_ORDER.indexOf(a.prayer || 'other');
    const bi = PRAYER_ORDER.indexOf(b.prayer || 'other');
    return ai - bi;
  });

  let nextIdx = -1;
  if (highlightNextMinyan) {
    let bestMin = Infinity;
    sorted.forEach((ct, i) => {
      if (ct.isActiveToday === false) return;
      const activeOverride = (ct.resolvedOverrides || ct.dayOverrides || []).find(ov => isOverrideActive(ov.days));
      const times = ct.type === 'recurring'
        ? (activeOverride ? [toMinutes(activeOverride.resolvedTime)] : (ct.resolvedSlots || []).map(toMinutes).filter(m => m >= 0))
        : (() => { const m = toMinutes(activeOverride ? activeOverride.resolvedTime : ct.resolvedTime); return m >= 0 ? [m] : []; })();
      for (const m of times) {
        if (m >= nowMin && m < bestMin) { bestMin = m; nextIdx = i; }
      }
    });
  }

  return (
    <div style={{
      height: '100%', width: '100%',
      display: 'flex', flexDirection: 'column',
      direction: 'rtl', padding: '20px', boxSizing: 'border-box',
      overflowY: 'auto', fontFamily: 'inherit'
    }}>
      {showTitle && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          paddingBottom: '16px', marginBottom: '16px',
          borderBottom: '2px solid #e5ddd3'
        }}>
          <BookOpen style={{ width: '22px', height: '22px', color: '#166534' }} />
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1e3a5f' }}>{title}</h2>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {sorted.map((ct, idx) => {
          const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
          const isRecurring = ct.type === 'recurring';
          const isLast = idx === sorted.length - 1;
          const rule = !isRecurring ? formatTimeRule(ct) : null;
          const isNext = highlightNextMinyan && idx === nextIdx;

          return (
            <div key={idx} style={{
              paddingBottom: isNext ? '10px' : (isLast ? 0 : '14px'),
              marginBottom: isLast ? 0 : '14px',
              borderBottom: isNext ? 'none' : (isLast ? 'none' : '1px solid #f1f5f9'),
              ...(isNext ? {
                padding: '10px',
                backgroundColor: '#eff6ff',
                border: '2px solid #1e3a5f',
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(30,58,95,0.12)',
              } : {}),
            }}>
              {isNext && (
                <span style={{
                  display: 'inline-block', marginBottom: '6px',
                  fontSize: '11px', fontWeight: 700, color: '#fff',
                  backgroundColor: '#1e3a5f', borderRadius: '10px', padding: '2px 10px',
                }}>המניין הבא</span>
              )}
              {/* Main prayer row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '19px', fontWeight: 700, color: '#1e3a5f', flexShrink: 0 }}>{label}</span>
                  {(!showRuleMode && rule || ct.daysLabel) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 4px', alignItems: 'baseline' }}>
                      {!showRuleMode && rule && (
                        <span style={{ whiteSpace: 'nowrap', fontSize: '12px', color: '#64748b', fontWeight: 500, lineHeight: 1 }}>
                          {rule}{ct.daysLabel ? ' ·' : ''}
                        </span>
                      )}
                      {ct.daysLabel && (
                        <span style={{ whiteSpace: 'nowrap', fontSize: '12px', color: '#64748b', fontWeight: 500, lineHeight: 1 }}>
                          {ct.daysLabel}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {isRecurring ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <span style={{
                      fontSize: '13px', color: '#166534', fontWeight: 600,
                      backgroundColor: '#dcfce7', borderRadius: '20px', padding: '3px 12px', flexShrink: 0
                    }}>
                      כל {ct.interval} דק׳
                    </span>
                    {ct.resolvedSlots?.length > 0 && (
                      <span style={{
                        fontSize: '16px', fontWeight: 600, color: '#475569',
                        fontVariantNumeric: 'tabular-nums', direction: 'ltr', flexShrink: 0
                      }}>
                        {ct.resolvedSlots[0]} — {ct.resolvedSlots[ct.resolvedSlots.length - 1]}
                      </span>
                    )}
                  </div>
                ) : (
                  showRuleMode && ct.type === 'relative' ? (
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'left' }}>
                      {rule}
                    </span>
                  ) : (
                    <span style={{
                      fontSize: '23px', fontWeight: 700, color: '#1e3a5f',
                      fontVariantNumeric: 'tabular-nums', direction: 'ltr'
                    }}>
                      {ct.resolvedTime}
                    </span>
                  )
                )}
              </div>

              {/* Recurring slots — full mode only */}
              {isRecurring && recurringDisplay === 'full' && ct.resolvedSlots?.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(54px, 1fr))',
                  gap: '4px 6px',
                  marginTop: '8px',
                  direction: 'ltr'
                }}>
                  {ct.resolvedSlots.map((slot, i) => (
                    <span key={i} style={{
                      fontSize: '13px', fontWeight: 600, color: '#475569',
                      fontVariantNumeric: 'tabular-nums', textAlign: 'center',
                      backgroundColor: '#f8fafc', borderRadius: '4px', padding: '2px 0'
                    }}>
                      {slot}
                    </span>
                  ))}
                </div>
              )}

              {/* Day overrides */}
              {(ct.resolvedOverrides || ct.dayOverrides)?.map((ov, oi) => {
                const dayLabels = (ov.days || []).map(d => DAY_NAMES[d]).filter(Boolean).join(' ');
                const time = ov.resolvedTime || ov.time || '--:--';
                const active = isOverrideActive(ov.days);
                return (
                  <div key={oi} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginTop: '6px', paddingRight: '12px', paddingLeft: '8px',
                    paddingTop: active ? '5px' : 0, paddingBottom: active ? '5px' : 0,
                    borderRadius: active ? '8px' : 0,
                    backgroundColor: active ? '#f0fdf4' : 'transparent',
                    border: active ? '1px solid #bbf7d0' : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {active && (
                        <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#16a34a', flexShrink: 0, display: 'inline-block' }} />
                      )}
                      <span style={{ fontSize: '14px', color: active ? '#15803d' : '#64748b', fontWeight: active ? 600 : 500 }}>{dayLabels}</span>
                    </div>
                    <span style={{
                      fontSize: '16px', fontWeight: 700, color: active ? '#15803d' : '#475569',
                      fontVariantNumeric: 'tabular-nums', direction: 'ltr'
                    }}>
                      {time}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles = {
  state: {
    width: '100%', padding: '20px',
    textAlign: 'center', direction: 'rtl',
    fontSize: '18px', color: '#64748b'
  }
};

export default Modern;
